import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Table, Card, Row, Col, Form, Button, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useAdminDataPipelines } from 'api/hooks/use-admin-data-pipelines';
import { useDebounce } from 'api/hooks/use-debounce';
import AppBadge from 'shared/badges/app-badge';
import SortArrow from 'shared/tables/sort-arrow';
import TableLoading from 'shared/tables/table-loading';
import TableFooter from 'shared/tables/table-footer';
import { updateOrdering } from 'shared/tables/utils';
import PerformancePayloadModal from './PerformancePayloadModal';

const COLUMNS = 6;

const STATUS_VARIANTS = {
  running: 'warning',
  success: 'success',
  error: 'danger',
  timeout: 'timeout'
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const numeric = Number(seconds);
  if (Number.isNaN(numeric)) return '—';
  if (numeric < 60) return `${numeric.toFixed(1)}s`;
  const minutes = Math.floor(numeric / 60);
  const remaining = Math.round(numeric % 60);
  return `${minutes}m ${remaining}s`;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const renderStatusBadge = (status) => (
  <AppBadge variant={STATUS_VARIANTS[status] || 'secondary'}>
    {capitalize(status)}
  </AppBadge>
);

const DEFAULT_PARAMS = {
  page: 1,
  perPage: 25,
  orderBy: 'executed_at',
  orderDirection: 'desc',
  search: '',
  municipality_id: '',
  status: '',
  min_duration: '',
  max_duration: ''
};

const hasActiveFilters = (params) =>
  Boolean(
    params.search ||
    params.municipality_id ||
    params.status ||
    params.min_duration ||
    params.max_duration
  );

const DataPipelinesList = () => {
  const [params, setParamsState] = useState(DEFAULT_PARAMS);
  const [searchInput, setSearchInput] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const debouncedSearch = useDebounce(searchInput, 300);

  const setParams = useCallback((newParams) => {
    setParamsState((prev) =>
      typeof newParams === 'function' ? newParams(prev) : newParams
    );
  }, []);

  useEffect(() => {
    setParams((prev) => ({ ...prev, search: debouncedSearch, page: 1 }));
  }, [debouncedSearch, setParams]);

  const updateFilter = (key, value) => {
    setParams((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      search: '',
      municipality_id: '',
      status: '',
      min_duration: '',
      max_duration: ''
    }));
  };

  const { data: response, isLoading, isSuccess, error } = useAdminDataPipelines({ params });

  const pipelines = response?.data;
  const meta = response?.meta;
  const filters = response?.filters;
  const filtersActive = hasActiveFilters(params);

  const handleSort = (column) => updateOrdering(column, params, setParams);

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error loading data pipelines</Alert.Heading>
        <p>{error.message}</p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Data Pipelines</h2>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4} lg={3}>
              <Form.Label className="fw-light mb-1">Function name</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="e.g. base_prep_accounts"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col xs={6} md={3} lg={3}>
              <Form.Label className="fw-light mb-1">Municipality</Form.Label>
              <Form.Select
                name="Municipality"
                value={params.municipality_id}
                onChange={(e) => updateFilter('municipality_id', e.target.value)}
              >
                <option value="">All municipalities</option>
                {(filters?.municipalities || []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2} lg={2}>
              <Form.Label className="fw-light mb-1">Status</Form.Label>
              <Form.Select
                name="Status"
                value={params.status}
                onChange={(e) => updateFilter('status', e.target.value)}
              >
                <option value="">All statuses</option>
                {(filters?.statuses || []).map((status) => (
                  <option key={status} value={status}>{capitalize(status)}</option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={1} lg={1}>
              <Form.Label className="fw-light mb-1">Min (s)</Form.Label>
              <Form.Control
                name="min"
                type="number"
                min="0"
                value={params.min_duration}
                onChange={(e) => updateFilter('min_duration', e.target.value)}
              />
            </Col>

            <Col xs={6} md={1} lg={1}>
              <Form.Label className="fw-light mb-1">Max (s)</Form.Label>
              <Form.Control
                name="max"
                type="number"
                min="0"
                value={params.max_duration}
                onChange={(e) => updateFilter('max_duration', e.target.value)}
              />
            </Col>

            <Col xs={12} md={1} lg={2} className="text-md-end">
              <Button
                variant="light"
                className="text-primary w-100 w-md-auto"
                onClick={clearFilters}
                disabled={!filtersActive}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th
              role="button"
              onClick={() => handleSort('executed_at')}
              className="text-nowrap"
            >
              Executed At
              <SortArrow
                column="executed_at"
                orderBy={params.orderBy}
                orderDirection={params.orderDirection}
              />
            </th>
            <th>Municipality</th>
            <th role="button" onClick={() => handleSort('function_name')}>
              Function
              <SortArrow
                column="function_name"
                orderBy={params.orderBy}
                orderDirection={params.orderDirection}
              />
            </th>
            <th role="button" onClick={() => handleSort('status')}>
              Status
              <SortArrow
                column="status"
                orderBy={params.orderBy}
                orderDirection={params.orderDirection}
              />
            </th>
            <th role="button" onClick={() => handleSort('execution_seconds')}>
              Duration
              <SortArrow
                column="execution_seconds"
                orderBy={params.orderBy}
                orderDirection={params.orderDirection}
              />
            </th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <TableLoading columns={COLUMNS} />}
          {isSuccess && pipelines?.map((pipeline) => (
            <tr
              key={pipeline.id}
              onClick={() => setSelectedPipeline(pipeline)}
              style={{ cursor: 'pointer' }}
            >
              <td className="text-nowrap">
                {pipeline.executed_at ? new Date(pipeline.executed_at).toLocaleString() : '—'}
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <Link to={`/admin/municipalities/${pipeline.municipality.id}`}>
                  {pipeline.municipality.name}
                </Link>
              </td>
              <td><code>{pipeline.function_name}</code></td>
              <td>{renderStatusBadge(pipeline.status_text)}</td>
              <td>{formatDuration(pipeline.execution_seconds)}</td>
              <td>
                {pipeline.message ? (
                  <span className={pipeline.status_text === 'error' ? 'text-danger' : ''}>
                    {pipeline.message}
                  </span>
                ) : <span className="text-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {isSuccess && pipelines?.length === 0 && (
        <Alert variant="info">No pipeline runs match the current filters.</Alert>
      )}

      {isSuccess && meta && (
        <TableFooter meta={meta} params={params} setParams={setParams} />
      )}

      <PerformancePayloadModal
        show={!!selectedPipeline}
        onHide={() => setSelectedPipeline(null)}
        dataUpdate={selectedPipeline}
      />
    </div>
  );
};

export default DataPipelinesList;
